import type { HeavenlyStem } from '../types/stems';
import type { FourPillars } from '../types/pillars';
import type { MonthlyFortune } from '../types/fortune';
import type { YongSin } from '../types/strength';
import type { GyeokGukAnalysis } from '../types/gyeok-guk';
import { HEAVENLY_STEMS, EARTHLY_BRANCHES, MONTH_STEM_START } from '../core/constants';
import { makePillarFromStemBranch } from '../core/sexagenary';
import { getTenGod } from '../analysis/ten-gods';
import { analyzeFortuneRelations } from '../relations/fortune-relations';

/**
 * 지정 연도의 월운을 계산한다.
 *
 * 월주 계산과 동일한 로직을 사용한다:
 * - 월지: 절기 경계 기준
 * - 월간: 년간 기반 오호둔월법
 */
export function getMonthlyFortune(
  dayMaster: HeavenlyStem,
  year: number,
  fourPillars?: FourPillars,
  noHour?: boolean,
  yongSin?: YongSin,
  gyeokGuk?: GyeokGukAnalysis,
): MonthlyFortune[] {
  const result: MonthlyFortune[] = [];

  const startStemIdx = getMonthStemStart(year);
  const prevStartStemIdx = getMonthStemStart(year - 1);

  // 양력 1월~12월 순서로 배열 (사용자 친화적)
  // 1월(丑月)은 전년도 년간 기준, 2~12월은 해당 년도 년간 기준

  for (let calendarMonth = 1; calendarMonth <= 12; calendarMonth++) {
    // 양력 1월 = 丑月(전년도 마지막 달), 2월 = 寅月, ..., 12월 = 子月
    const monthOffset = calendarMonth === 1 ? 11 : calendarMonth - 2;
    const branchIdx = (2 + monthOffset) % 12;
    const branch = EARTHLY_BRANCHES[branchIdx];

    // 1월은 전년도 년간 기준 오호둔월법
    const usedStartStemIdx = calendarMonth === 1 ? prevStartStemIdx : startStemIdx;
    const monthStemIdx = (usedStartStemIdx + monthOffset) % 10;
    const stem = HEAVENLY_STEMS[monthStemIdx];

    const pillar = makePillarFromStemBranch(stem, branch);

    result.push({
      year,
      month: calendarMonth,
      pillar,
      tenGod: getTenGod(dayMaster, stem),
      relations: fourPillars ? analyzeFortuneRelations(fourPillars, pillar, noHour, yongSin, gyeokGuk) : [],
    });
  }

  return result;
}

/** 해당 연도의 오호둔월법 시작 천간 인덱스 */
function getMonthStemStart(y: number): number {
  const yearStemIdx = ((y - 4) % 10 + 10) % 10;
  return MONTH_STEM_START[yearStemIdx % 5];
}
